import React, { useState, useEffect } from "react";
import CardLayout from "../../../../common/cardLayout/CardLayout";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { generateYAxisTicks } from "../../../../../util/helpers";
import PatientLoader from "../../../../common/PatientLoader";
import { NoAssessments } from "../FlaggedJournals";
import { max, sum } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AssessmentsOverview = ({
  setCheckinTab = null,
  size = "7",
  showHeading = true,
  setCheckins = null,
}) => {
  const { assessmentsLoading, assessmentsData } = useSelector(
    (state) => state.assessment
  );

  const { singlePatientData } = useSelector((state) => state.patient);

  function normailzeData(data) {
    const formattedData = {};
    data.forEach((item) => {
      const date = moment(item?.created).format("MMM DD");
      if (date in formattedData) {
        formattedData[date] = item?.score;
      } else {
        formattedData[date] = item?.score;
      }
    });
    return formattedData;
  }

  function generateXAxisData(arr1, arr2, arr3) {
    arr1 = Object.keys(arr1);
    arr2 = Object.keys(arr2);
    arr3 = Object.keys(arr3);
    const res = [...new Set([...arr1, ...arr2, ...arr3])];
    return res;
  }

  const [labels, setLabels] = useState([]);
  const [mood, setMood] = useState([]);
  const [sleep, setSleep] = useState([]);
  const [anxiety, setAnxiety] = useState([]);
  const [noData, setNoData] = useState(false);
  const [yAxisLookup, setYAxisLookup] = useState({
    "-1": "",
    4: "😭 Awful",
    3: "😢 Bad",
    2: "🙂 Okay",
    1: "😁 Good",
    0: "😄 Great",
    5: "",
  });
  useEffect(() => {
    if (!assessmentsLoading) {
      if (assessmentsData && "Journal-Mood" in assessmentsData) {
        const condition1 =
          "Journal-Mood" in assessmentsData &&
          assessmentsData["Journal-Mood"]?.length !== 0;
        const condition2 =
          "Journal-Anxiety" in assessmentsData &&
          assessmentsData["Journal-Anxiety"]?.length !== 0;
        const condition3 =
          "Journal-Sleep" in assessmentsData &&
          assessmentsData["Journal-Sleep"]?.length !== 0;
        if (!condition1 && !condition2 && !condition3) {
          setNoData(true);
          return;
        }
        setNoData(false);
        const data1 = normailzeData(assessmentsData["Journal-Anxiety"]);
        setAnxiety(Object.values(data1));
        const data2 = normailzeData(assessmentsData["Journal-Mood"]);
        setMood(Object.values(data2));
        const data3 = normailzeData(assessmentsData["Journal-Sleep"]);
        setSleep(Object.values(data3));
        const xAxis = generateXAxisData(data1, data2, data3);
        setLabels([...xAxis]);
      }
    }
  }, [assessmentsLoading, assessmentsData]);

  useEffect(() => {
    if (labels?.length > 0) {
      if (setCheckins) {
        setCheckins(max([mood?.length, sleep?.length, anxiety?.length]));
      }
    }
  }, [labels]);

  const data = [
    {
      label: "Mood",
      data: mood,
      borderColor: "#FFB200",
      backgroundColor: "#FFB200",
    },
    {
      label: "Anxiety",
      data: anxiety,
      borderColor: "#34B53A",
      backgroundColor: "#34B53A",
    },
    {
      label: "Sleep",
      data: sleep,
      borderColor: "#4339F2",
      backgroundColor: "#4339F2",
    },
  ];

  if (process.env.REACT_APP_DASHBOARD_TYPE === "youth") {
    data.pop();
  }
  const chartData = {
    labels,
    datasets: data,
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        reverse: true,
        min: -1,
        max: 5,
        ticks: {
          callback: (value) => {
            return yAxisLookup[value];
          },
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          boxHeight: 10,
          useBorderRadius: true,
          borderRadius: 100,
          pointStyle: "circle",
          family: "'DM Sans'",
        },
        position: "top",
        align: "start",
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <CardLayout
      className={`col-sm-12 col-md-${size} col-lg-${size}`}
      style={{
        border: "8px solid transparent",
        boxSizing: "border-box",
        backgroundClip: "padding-box",
      }}
    >
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between mb-4 mt-2">
          {showHeading && (
            <label style={{ fontSize: "1.3rem" }}>Check in Overview</label>
          )}

          {setCheckinTab !== null ? (
            <small
              onClick={() => {
                setCheckinTab();
              }}
              style={{
                color: "#395e9d",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              View more {">"}
            </small>
          ) : (
            <></>
          )}
        </div>
        {noData ? (
          <NoAssessments
            text={`No data available for ${
              singlePatientData?.first_name || "Patient"
            }!`}
            show_link={false}
          />
        ) : (
          <>
            {assessmentsLoading ? (
              <PatientLoader showText={false} />
            ) : (
              <div className="container-fluid">
                <Line options={options} data={chartData} />
              </div>
            )}
          </>
        )}
      </div>
    </CardLayout>
  );
};

export default AssessmentsOverview;
